import {
	AlertCircle,
	FileText,
	ImageIcon,
	Plus,
	Receipt,
	RefreshCw,
	Search,
	Sparkles,
	X,
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

import { CategoryBadge } from '@/components/category-badge'
import { CategoryMultiSelect } from '@/components/category-multi-select'
import { EmptyState } from '@/components/empty-state'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { WarrantyBadge } from '@/components/warranty-badge'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatDate, formatPrice } from '@/lib/format'
import { type RouterOutputs, trpc } from '@/trpc/client'

type ItemsList = RouterOutputs['items']['list']
type Item = ItemsList['items'][number]

type SortBy =
	| 'purchaseDateDesc'
	| 'purchaseDateAsc'
	| 'priceDesc'
	| 'priceAsc'
	| 'nameAsc'

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
	{ value: 'purchaseDateDesc', label: 'Newest purchase' },
	{ value: 'purchaseDateAsc', label: 'Oldest purchase' },
	{ value: 'priceDesc', label: 'Highest price' },
	{ value: 'priceAsc', label: 'Lowest price' },
	{ value: 'nameAsc', label: 'Name (A → Z)' },
]

function parseNumber(value: string | null): number | undefined {
	if (!value) return undefined
	const n = Number(value)
	return Number.isFinite(n) && n >= 0 ? n : undefined
}

function parseDate(value: string | null): Date | undefined {
	if (!value) return undefined
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? undefined : d
}

export function ItemsListPage() {
	const [searchParams, setSearchParams] = useSearchParams()

	const search = searchParams.get('q') ?? ''
	const minPrice = searchParams.get('min') ?? ''
	const maxPrice = searchParams.get('max') ?? ''
	const fromDate = searchParams.get('from') ?? ''
	const toDate = searchParams.get('to') ?? ''
	const categoryIds = searchParams.getAll('cat')
	const sortBy =
		(searchParams.get('sort') as SortBy | null) ?? 'purchaseDateDesc'

	const debouncedSearch = useDebouncedValue(search, 300)

	function setParam(key: string, value: string | null) {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				if (!value) next.delete(key)
				else next.set(key, value)
				return next
			},
			{ replace: true },
		)
	}

	function setCategories(ids: string[]) {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.delete('cat')
				for (const id of ids) next.append('cat', id)
				return next
			},
			{ replace: true },
		)
	}

	function clearFilters() {
		setSearchParams(new URLSearchParams(), { replace: true })
	}

	const hasFilters =
		search ||
		minPrice ||
		maxPrice ||
		fromDate ||
		toDate ||
		categoryIds.length > 0 ||
		sortBy !== 'purchaseDateDesc'

	const categoriesQuery = trpc.categories.list.useQuery()
	const orgQuery = trpc.organizations.getCurrent.useQuery()

	const itemsQuery = trpc.items.list.useQuery({
		search: debouncedSearch || undefined,
		minPrice: parseNumber(minPrice),
		maxPrice: parseNumber(maxPrice),
		fromDate: parseDate(fromDate),
		toDate: parseDate(toDate),
		categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
		sortBy,
	})

	return (
		<div className="space-y-8">
			<PageHeader
				title="Items"
				description="All your receipts and invoices in one place."
				actions={
					<Button asChild>
						<Link to="/app/items/new">
							<Plus className="size-4" />
							New item
						</Link>
					</Button>
				}
			/>

			<CapacityBanner org={orgQuery.data} />

			<Filters
				search={search}
				onSearchChange={(v) => setParam('q', v)}
				minPrice={minPrice}
				onMinPriceChange={(v) => setParam('min', v)}
				maxPrice={maxPrice}
				onMaxPriceChange={(v) => setParam('max', v)}
				fromDate={fromDate}
				onFromDateChange={(v) => setParam('from', v)}
				toDate={toDate}
				onToDateChange={(v) => setParam('to', v)}
				categoryIds={categoryIds}
				onCategoriesChange={setCategories}
				categoryOptions={categoriesQuery.data ?? []}
				categoriesLoading={categoriesQuery.isPending}
				sortBy={sortBy}
				onSortChange={(v) =>
					setParam('sort', v === 'purchaseDateDesc' ? null : v)
				}
				onClear={hasFilters ? clearFilters : undefined}
			/>

			<ItemsResults query={itemsQuery} hasFilters={Boolean(hasFilters)} />
		</div>
	)
}

interface FiltersProps {
	search: string
	onSearchChange: (v: string) => void
	minPrice: string
	onMinPriceChange: (v: string) => void
	maxPrice: string
	onMaxPriceChange: (v: string) => void
	fromDate: string
	onFromDateChange: (v: string) => void
	toDate: string
	onToDateChange: (v: string) => void
	categoryIds: string[]
	onCategoriesChange: (ids: string[]) => void
	categoryOptions: { id: string; name: string; color: string }[]
	categoriesLoading: boolean
	sortBy: SortBy
	onSortChange: (v: SortBy) => void
	onClear?: () => void
}

function Filters(props: FiltersProps) {
	return (
		<div className="rounded-xl border bg-card p-4 sm:p-5 shadow-xs">
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
				<div className="lg:col-span-5">
					<Label
						htmlFor="search-input"
						className="text-xs text-muted-foreground"
					>
						Search
					</Label>
					<div className="relative mt-1.5">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							id="search-input"
							placeholder="Name or store…"
							value={props.search}
							onChange={(e) => props.onSearchChange(e.target.value)}
							className="pl-9"
						/>
					</div>
				</div>

				<div className="lg:col-span-4">
					<Label className="text-xs text-muted-foreground">Categories</Label>
					<div className="mt-1.5">
						<CategoryMultiSelect
							options={props.categoryOptions}
							value={props.categoryIds}
							onChange={props.onCategoriesChange}
							disabled={props.categoriesLoading}
						/>
					</div>
				</div>

				<div className="lg:col-span-3">
					<Label className="text-xs text-muted-foreground">Sort by</Label>
					<div className="mt-1.5">
						<Select
							value={props.sortBy}
							onValueChange={(v) => props.onSortChange(v as SortBy)}
						>
							<SelectTrigger className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{SORT_OPTIONS.map((o) => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="lg:col-span-3">
					<Label className="text-xs text-muted-foreground">Min price</Label>
					<Input
						className="mt-1.5"
						type="number"
						min={0}
						step="0.01"
						placeholder="0"
						value={props.minPrice}
						onChange={(e) => props.onMinPriceChange(e.target.value)}
					/>
				</div>
				<div className="lg:col-span-3">
					<Label className="text-xs text-muted-foreground">Max price</Label>
					<Input
						className="mt-1.5"
						type="number"
						min={0}
						step="0.01"
						placeholder="∞"
						value={props.maxPrice}
						onChange={(e) => props.onMaxPriceChange(e.target.value)}
					/>
				</div>
				<div className="lg:col-span-3">
					<Label className="text-xs text-muted-foreground">From date</Label>
					<Input
						className="mt-1.5"
						type="date"
						value={props.fromDate}
						onChange={(e) => props.onFromDateChange(e.target.value)}
					/>
				</div>
				<div className="lg:col-span-3">
					<Label className="text-xs text-muted-foreground">To date</Label>
					<Input
						className="mt-1.5"
						type="date"
						value={props.toDate}
						onChange={(e) => props.onToDateChange(e.target.value)}
					/>
				</div>
			</div>

			{props.onClear ? (
				<div className="mt-4 flex justify-end">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={props.onClear}
					>
						<X className="size-4" />
						Clear filters
					</Button>
				</div>
			) : null}
		</div>
	)
}

interface ItemsResultsProps {
	query: {
		isPending: boolean
		isError: boolean
		error: { message: string } | null
		data: ItemsList | undefined
		refetch: () => void
	}
	hasFilters: boolean
}

function ItemsResults({ query, hasFilters }: ItemsResultsProps) {
	if (query.isPending || !query.data) {
		return (
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{Array.from({ length: 6 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
					<Skeleton key={i} className="h-44 w-full" />
				))}
			</div>
		)
	}

	if (query.isError) {
		return (
			<Alert variant="destructive">
				<AlertCircle />
				<AlertTitle>Couldn't load your items</AlertTitle>
				<AlertDescription>
					{query.error?.message ?? 'Unknown error'}
					<div className="mt-2">
						<Button variant="outline" size="sm" onClick={() => query.refetch()}>
							<RefreshCw className="size-4" />
							Retry
						</Button>
					</div>
				</AlertDescription>
			</Alert>
		)
	}

	const items = query.data.items
	if (items.length === 0) {
		if (hasFilters) {
			return (
				<EmptyState
					icon={Search}
					title="No items match your filters"
					description="Try clearing some filters or broadening your search."
				/>
			)
		}
		return (
			<EmptyState
				icon={Receipt}
				title="No items yet"
				description="Add your first receipt or invoice to start tracking warranties."
				action={
					<Button asChild>
						<Link to="/app/items/new">
							<Plus className="size-4" />
							Add an item
						</Link>
					</Button>
				}
			/>
		)
	}

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground tabular-nums">
				{query.data.total} {query.data.total === 1 ? 'item' : 'items'}
			</p>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{items.map((item) => (
					<ItemCard key={item.id} item={item} />
				))}
			</div>
		</div>
	)
}

function CapacityBanner({
	org,
}: {
	org: RouterOutputs['organizations']['getCurrent'] | undefined
}) {
	if (!org) return null
	if (org.plan === 'pro') return null
	const pct = org.itemsCount / org.limits.items
	if (pct < 0.8) return null
	return (
		<div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
			<div className="flex items-center gap-2 min-w-0">
				<Sparkles className="size-4 shrink-0" />
				<span className="truncate">
					You're using <strong>{org.itemsCount}</strong> of{' '}
					<strong>{org.limits.items}</strong> items on the Free plan.
				</span>
			</div>
			<Button asChild size="sm" variant="outline" className="shrink-0">
				<Link to="/app/settings/billing">Upgrade</Link>
			</Button>
		</div>
	)
}

function ItemCard({ item }: { item: Item }) {
	const FileIcon =
		item.fileMimeType === 'application/pdf'
			? FileText
			: item.fileMimeType?.startsWith('image/')
				? ImageIcon
				: Receipt
	return (
		<Link to={`/app/items/${item.id}`} className="group block">
			<Card className="h-full gap-4 py-4 transition-all hover:border-primary/40 hover:shadow-md">
				<CardContent className="space-y-3">
					<div className="flex items-start gap-3">
						<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
							<FileIcon className="size-5" />
						</div>
						<div className="min-w-0 flex-1">
							<p className="truncate font-medium leading-tight group-hover:text-primary">
								{item.name}
							</p>
							{item.store ? (
								<p className="mt-0.5 truncate text-xs text-muted-foreground">
									{item.store}
								</p>
							) : null}
						</div>
					</div>
					<div className="flex items-baseline justify-between gap-2">
						<p className="text-lg font-semibold tabular-nums tracking-tight">
							{formatPrice(item.price, item.currency)}
						</p>
						<p className="text-xs tabular-nums text-muted-foreground">
							{formatDate(item.purchaseDate)}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-1.5">
						<WarrantyBadge expiresAt={item.warrantyExpiresAt} />
						{item.categories.slice(0, 2).map((cat) => (
							<CategoryBadge key={cat.id} name={cat.name} color={cat.color} />
						))}
						{item.categories.length > 2 ? (
							<span className="text-xs text-muted-foreground">
								+{item.categories.length - 2}
							</span>
						) : null}
					</div>
				</CardContent>
			</Card>
		</Link>
	)
}
